import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';
import { setAnalyticsEnabled } from '../firebase';

const COOKIE_CONSENT_KEY = 'studytino_cookie_consent';

interface CookieConsent {
    analytics: boolean;
}

const CookieBanner = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [consent, setConsent] = useState<CookieConsent | null>(null);
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setConsent(parsed);
                // Analytics-Status gemäß gespeicherter Einwilligung setzen
                setAnalyticsEnabled(parsed.analytics === true);
                setShowBanner(false);
            } catch (e) {
                setShowBanner(true);
            }
        } else {
            setShowBanner(true);
        }
    }, []);

    const handleAcceptAll = () => {
        const newConsent: CookieConsent = { analytics: true };
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(newConsent));
        setConsent(newConsent);
        setShowBanner(false);
        setAnalyticsEnabled(true);
    };

    const handleDecline = () => {
        const newConsent: CookieConsent = { analytics: false };
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(newConsent));
        setConsent(newConsent);
        setShowBanner(false);
        setAnalyticsEnabled(false);
    };

    const handleSavePreferences = () => {
        const analyticsChecked = (document.getElementById('analytics-checkbox') as HTMLInputElement)?.checked || false;
        const newConsent: CookieConsent = { analytics: analyticsChecked };
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(newConsent));
        setConsent(newConsent);
        setShowBanner(false);
        setAnalyticsEnabled(analyticsChecked);
    };

    if (!showBanner) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white/90 backdrop-blur-md border-t border-gray-200 shadow-lg"
            >
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex-1 flex items-start gap-3">
                        <Cookie className="w-6 h-6 text-indigo-600 mt-1 flex-shrink-0" />
                        <div>
                            <p className="text-sm text-gray-700">
                                Wir verwenden Cookies, um die Funktionalität der Website zu gewährleisten und mit Ihrer Einwilligung anonyme Nutzungsstatistiken zu erheben (Firebase Analytics).
                                Weitere Informationen finden Sie in unserer <Link to="/datenschutz" className="text-indigo-600 hover:underline" target="_blank">Datenschutzerklärung</Link>.
                            </p>
                            {showDetails && (
                                <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Cookie‑Einstellungen</h4>
                                    <label className="flex items-center gap-2 text-sm text-gray-700">
                                        <input type="checkbox" id="analytics-checkbox" defaultChecked={consent?.analytics ?? false} />
                                        Analyse‑Cookies (Firebase Analytics)
                                    </label>
                                    <div className="mt-2 flex gap-2">
                                        <button
                                            onClick={handleSavePreferences}
                                            className="px-4 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition"
                                        >
                                            Auswahl speichern
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                        <button
                            onClick={() => setShowDetails(!showDetails)}
                            className="text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                            {showDetails ? 'Weniger' : 'Einstellungen'}
                        </button>
                        <button
                            onClick={handleDecline}
                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                        >
                            Ablehnen
                        </button>
                        <button
                            onClick={handleAcceptAll}
                            className="px-6 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition shadow-md"
                        >
                            Alle akzeptieren
                        </button>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default CookieBanner;